<template>
  <b-container>
    <h1 v-translate>Company Area</h1>
    <hr>
    <div class="row">
      <div class="col-md-3">
        <company-navigation />
      </div>
      <div class="col-md-9 company-profile">
        <h3 v-translate>Company Profile</h3>
        <b-alert variant="info" show v-if="!!sla.id">
          <p class="m-0">
            <span v-translate="{slaDate: moment.utc(sla.created_at).tz(this.$tz).format('L LT')}">Current SLA document, uploaded on %{slaDate}</span>
            <b-button size="sm" style="padding: 0.1rem 1rem;" :href="'/download?f=companies/' + company.id + '/documents/' + sla.full_path.split('/')[sla.full_path.split('/').length - 1]" target="_blank" v-translate>Download</b-button>
          </p>
        </b-alert>

        <form @submit.prevent="updateCompany">
          <h4 class="mt-4" v-translate>Basic Info <small class="text-danger">(required)</small></h4>
          <b-row>
            <b-col cols="8">
              <b-form-group v-if="accountingCatalogs.filter(x => x.catalog === 'states').length > 0">
                <b-form-select size="lg" v-model="company.tin_type" :options="accountingCatalogs.filter(x => x.catalog === 'document_types').map(x => { return {text: x.name, value: x.id}})" class="form-control" disabled>
                  <template #first>
                    <option :value="null" v-translate>Document Type</option>
                  </template>
                </b-form-select>
              </b-form-group>
              <div class="form-group">
                <input-facade v-if="tinCountry && $appConfig.tinMask[tinCountry]" :mask="$appConfig.tinMask[tinCountry] || ''" :placeholder="$gettext('TIN')" v-model="company.tin" class="form-control" id="company-tin" disabled required />
                <b-input v-else class="form-control" id="company-tin" disabled :placeholder="$gettext('TIN')" v-model="company.tin" required />
              </div>
              <b-form-group v-if="accountingCatalogs.filter(x => x.catalog === 'customer_types').length > 0">
                <b-form-select size="lg" v-model="company.entity_type" :options="accountingCatalogs.filter(x => x.catalog === 'customer_types').map(x => { return {text: x.name, value: x.id}})" class="form-control" disabled>
                  <template #first>
                    <option :value="null" v-translate>Client Type</option>
                  </template>
                </b-form-select>
              </b-form-group>
              <b-form-group v-if="accountingCatalogs.filter(x => x.catalog === 'tax_conditions').length > 0">
                <b-form-select size="lg" v-model="company.contributor_type" :options="accountingCatalogs.filter(x => x.catalog === 'tax_conditions').map(x => { return {text: x.name, value: x.id}})" class="form-control" disabled>
                  <template #first>
                    <option :value="null" v-translate>Contributor Type</option>
                  </template>
                </b-form-select>
              </b-form-group>
              <div class="form-group">
                <input class="form-control" id="company__business_name" :placeholder="$gettext('Fiscal/Legal Name')" v-model="company.business_name" disabled>
              </div>
              <div class="form-group">
                <input class="form-control" id="company__name" :placeholder="$gettext('Brand/Commercial Name')" v-model="company.name" required>
              </div>
              <div class="form-group">
                <b-textarea class="form-control" id="company__address" :placeholder="$gettext('Fiscal/Legal Address')" v-model="company.address" disabled />
              </div>
              <b-form-group v-if="accountingCatalogs.filter(x => x.catalog === 'states').length > 0">
                <b-form-select size="lg" v-model="company.address_state" :options="accountingCatalogs.filter(x => x.catalog === 'states').map(x => { return {text: x.name, value: x.id}})" class="form-control" disabled>
                  <template #first>
                    <option :value="null" v-translate>Province/State</option>
                  </template>
                </b-form-select>
              </b-form-group>
            </b-col>
            <b-col class="text-center">
              <div v-if="company.logo">
                <div class="logo-container" :style="{backgroundImage: 'url(' + company.logo + ')'}">
                  <b-img v-if="company.logo" style="width:100%; visibility: hidden;" :src="company.logo" blank-color="#777" alt="img" />
                </div>
                <b-button class="mt-3" v-b-modal.modalUploadImage>
                  <translate>Change Logo</translate>
                </b-button>
              </div>
              <div v-if="!company.logo" class="no-logo-container d-flex text-center">
                <div class="align-self-center w-100">
                  <b-button v-b-modal.modalUploadImage v-translate>Add Logo</b-button>
                </div>
              </div>
            </b-col>
          </b-row>
          <hr class="my-4">
          <h5 v-if="!isSelfService" class="mt-4" v-translate>Industries <small class="text-danger">(required)</small></h5>
          <div v-if="!isSelfService" class="form-group">
            <b-form-checkbox-group v-model="marketingSettings" disabled>
              <b-form-checkbox class="w-50 mr-0" v-for="(i, index) in industries.map(x => {return {text: x.description, value: x.id}})" :value="i.value" :key="index">{{i.text === 'Other' ? $pgettext('fem', 'Other') : i.text}}</b-form-checkbox>
            </b-form-checkbox-group>
          </div>
          <hr v-if="!isSelfService" class="my-4">
          <b-row>
            <h5 class="col-12" v-translate>Commercial Contact <small class="text-danger">(required)</small></h5>
            <div class="form-group col-12 col-md-6">
              <input type="text" :placeholder="$gettext('Name')" class="form-control" id="company__commercial__name" v-model="company.contact_name" required>
            </div>
            <div class="form-group col-12 col-md-6">
              <input type="text" :placeholder="$gettext('Phone')" class="form-control" id="company__commercial__phone" v-model="company.phone" required>
            </div>
            <div class="form-group col-12 col-md-6">
              <input type="email" :placeholder="$gettext('E-mail')" class="form-control" id="company__commercial__email" v-model="company.email" required>
            </div>
          </b-row>
          <hr class="my-4">
          <b-row v-if="!isSelfService">
            <h5 class="col-12" v-translate>Billing Contact <small class="text-danger">(required)</small></h5>
            <div class="form-group col-12 col-md-6">
              <input type="text" :placeholder="$gettext('Name')" class="form-control" id="company__billing__name" v-model="company.billing_details.name" required>
            </div>
            <div class="form-group col-12 col-md-6">
              <input type="text" :placeholder="$gettext('Last Name')" class="form-control" id="company__billing__last_name" v-model="company.billing_details.last_name" required>
            </div>
            <div class="form-group col-12 col-md-6">
              <input type="text" :placeholder="$gettext('Phone')" class="form-control" id="company__billing__phone" v-model="company.billing_details.phone" required>
            </div>
            <div class="form-group col-12 col-md-6" v-for="(item, index) in company.billing_details.emails" :key="index">
              <b-input-group>
                <b-input type="email" :placeholder="$gettext('E-mail')" class="form-control" :id="'company__billing__email' + index" v-model="company.billing_details.emails[index]" required />
                <b-input-group-append>
                  <b-button size="sm" variant="danger" @click="removeBillingDetailsEmail(index)" style="padding-top: initial; padding-bottom: initial;">
                      <strong>✕</strong>
                  </b-button>
                </b-input-group-append>
              </b-input-group>
            </div>
            <form @submit.prevent="addBillingDetailsEmail" v-if="company.billing_details.emails.length < 5" class=" col-12 col-md-6">
              <b-input-group>
                <input type="email" :placeholder="$gettext('E-mail')" class="form-control" id="company__billing__email" v-model="newBillingDetailsEmail" required @blur="$refs.argh.click()" />
                <b-input-group-append>
                  <b-button size="sm" ref="argh" variant="success" :disabled="newBillingDetailsEmail === ''" type="submit" style="padding-top: initial; padding-bottom: initial;">
                      <strong>＋</strong>
                  </b-button>
                </b-input-group-append>
              </b-input-group>
              <b-form-text v-translate>Up to 5 e-mail addresses that will receive our invoices.</b-form-text>
            </form>

            <b-col cols="12">
              <hr class="my-4">
              <h3 v-translate>Technical Contact Information</h3>
              <p v-translate="{siteName: this.siteName}">Name and e-mail address of the Technical Contact person in your Company. We will send all emails and communications regarding your Company operation webhooks/API applications/technical news/etc. in %{siteName} to this contact person. Please, keep it updated.</p>
              <p><small v-translate>Note: If you don't set a Technical Contact all technical communications will be sent to the Commercial Contact.</small></p>
            </b-col>
            <b-col md="6" class="form-group">
              <input type="text" :placeholder="$gettext('Name')" class="form-control" v-model="company.technical_contact_name">
            </b-col>
            <b-col md="6" class="form-group">
              <input type="email" :placeholder="$gettext('E-mail')" class="form-control" v-model="company.technical_contact_email">
            </b-col>

            <b-col cols="12">
              <hr class="my-4">
              <h3 v-translate>Operations Contact Information</h3>
              <p v-translate="{siteName: this.siteName}">Name and e-mail address of the Operations Contact person in your Company. We will send all emails and communications regarding your Company Operation (pending pickups/etc.) in %{siteName} to this contact person. Please, keep it updated.</p>
              <p><small v-translate>Note: If you don't set an Operations Contact all operation communications will be sent to the Commercial Contact.</small></p>
            </b-col>
            <b-col md="6" class="form-group">
              <input type="text" :placeholder="$gettext('Name')" class="form-control" v-model="company.operations_contact_name">
            </b-col>
            <b-col md="6" class="form-group">
              <input type="email" :placeholder="$gettext('E-mail')" class="form-control" v-model="company.operations_contact_email">
            </b-col>

          </b-row>
          <hr v-if="!isSelfService" class="my-4">
          <h5 v-if="!isSelfService" class="mt-4" v-translate>Other Info <small class="text-muted">(optional)</small></h5>
          <b-row v-if="!isSelfService">
            <b-col>
              <h6 v-translate>E-commerce Technologies</h6>
              <div class="form-group">
                <b-form-checkbox-group v-model="marketingSettings" stacked :options="techs.map(x => {return {text: x.description, value: x.id}})">
                </b-form-checkbox-group>
              </div>
            </b-col>
          </b-row>
          <b-button variant="primary" type="submit" class="mt-3">
            <translate>Update Profile</translate>
          </b-button>
        </form>
      </div>
    </div>
    <b-modal id="modalUploadImage" ref="modalUploadImage" :hide-footer="true">
      <div slot="modal-title" v-translate>Add a new logo image</div>
      <div slot="modal-header-close"><img src="@/assets/img/ic_close.svg" alt="×" /></div>
      <profile-picture-uploader @updateProfileImage="updateCompanyImage" />
    </b-modal>
  </b-container>
</template>

<script>
import { FETCH_PROFILE } from '@/store/actions.type'
import CompanyNavigation from '@/components/CompanyNavigation'
import ProfilePictureUploader from '@/components/ProfilePictureUploader'

export default {
  name: 'CompanyProfilePage',
  title() {
    return this.$gettext('Company Profile')
  },
  components: {
    CompanyNavigation,
    ProfilePictureUploader
  },
  data() {
    return {
      company: {},
      techs: [],
      industries: [],
      marketingSettings: [],
      newBillingDetailsEmail: '',
      accountingCatalogs: [],
      sla: {}
    }
  },
  watch: {
    'company.billing_details': {
      handler(val) {
        if (!val) {
          this.company.billing_details = {
            emails: []
          }
        }
      },
      deep: true
    }
  },
  computed: {
    tinCountry() {
      return window.countries.find(x => x.iso_code === this.company.country_iso_code).language.split('_')[1]
    }
  },
  created() {
    this.fetchMktgStuff()
  },
  mounted() {
    this.getCompany()
  },
  methods: {
    getCompany() {
      this.$store.dispatch(FETCH_PROFILE)
        .then(response => {
          this.company = response.company
          this.marketingSettings = [...new Set(this.company.marketing_settings)].map(x => {
            return x.setting.id
          })
          this.fetchCompanyDocuments()
          this.fetchAccountingStuff()
        })
    },
    fetchCompanyDocuments() {
      const params = {
        sort: [{
          key: 'created_at',
          direction: 'desc'
        }],
        limit: 1,
        filter_groups: [
          {
            filters: [
              ['type', 'eq', 'sla']
            ]
          }
        ]
      }
      this.$http.get(this.$appConfig.api.baseUrl + 'companies/' + this.company.id + '/documents', { params })
        .then(response => {
          this.sla = response.body.data[0]
        })
    },
    fetchMktgStuff() {
      let params = {
        filter_groups: [
          {
            filters: [
              ['type', 'eq', 'e-commerce-tech']
            ]
          }
        ]
      }
      this.$http.get(this.$appConfig.api.baseUrl + 'marketing-catalogs', { params })
        .then(response => {
          this.techs = response.body.data
        })

      params = {
        filter_groups: [
          {
            filters: [
              ['type', 'eq', 'industry']
            ]
          }
        ]
      }
      this.$http.get(this.$appConfig.api.baseUrl + 'marketing-catalogs', { params })
        .then(response => {
          this.industries = response.body.data
        })
    },
    addBillingDetailsEmail() {
      this.company.billing_details.emails.push(this.newBillingDetailsEmail)
      this.newBillingDetailsEmail = ''
    },
    removeBillingDetailsEmail(index) {
      this.company.billing_details.emails.splice(index, 1)
    },
    updateCompany() {
      delete this.company.tin
      delete this.company.country_iso_code
      delete this.company.cut_time_week
      delete this.company.cut_time_saturday
      delete this.company.cut_time_sunday

      this.$http.patch(this.$appConfig.api.baseUrl + 'companies/' + this.company.id, this.company)
        .then(response => {
          this.$refs.modalUploadImage.hide()
          if (JSON.stringify(this.marketingSettings.sort()) !== JSON.stringify(this.company.marketing_settings.map(x => {
            return x.setting.id
          }).sort())) {
            this.updateMarketingSettings()
          } else {
            this.getCompany()
          }
        }, error => {
          let message = ''
          if (error.status === 422) {
            for (let i = 0; i < error.body.errors.length; i++) {
              message += '- ' + error.body.errors[i].detail + '<br>'
            }
          } else {
            message = error.body.message
          }
          const obj = {
            title: 'ERROR',
            message,
            type: 'error',
            customCloseBtnText: this.$gettext('Close')
          }
          this.$root.$children[0].$refs.simplert.openSimplert(obj)
        })
    },
    async updateMarketingSettings() {
      if (this.company.marketing_settings.length > 0) {
        this.company.marketing_settings.forEach((item, i) => {
          this.$http.delete(this.$appConfig.api.baseUrl + 'companies/' + this.company.id + '/marketing-settings/' + item.id)
            .then(() => {
              if (this.company.marketing_settings.length === i + 1) {
                let payload
                this.marketingSettings.forEach((item2, n) => {
                  payload = {
                    company_id: this.company.id,
                    marketing_catalog_id: item2
                  }
                  this.$http.post(this.$appConfig.api.baseUrl + 'companies/' + this.company.id + '/marketing-settings', payload)
                    .then(response => {
                      if (this.marketingSettings.length === n + 1) {
                        setTimeout(() => {
                          this.getCompany()
                        }, 1000)
                      }
                    })
                })
              }
            })
        })
      } else {
        let payload
        this.marketingSettings.forEach((item, i) => {
          payload = {
            company_id: this.company.id,
            marketing_catalog_id: item
          }
          this.$http.post(this.$appConfig.api.baseUrl + 'companies/' + this.company.id + '/marketing-settings', payload)
            .then(response => {
              if (this.marketingSettings.length === i + 1) {
                setTimeout(() => {
                  this.getCompany()
                }, 1000)
              }
            })
        })
      }
    },
    updateCompanyImage(url) {
      this.company.logo = url
      this.$http.patch(this.$appConfig.api.baseUrl + 'companies/' + this.company.id, { logo: url })
        .then(response => {
          this.$refs.modalUploadImage.hide()
          this.showAlert()
        }, error => {
          const obj = {
            title: 'ERROR',
            message: error.body.message,
            type: 'error'
          }
          this.$parent.$refs.simplert.openSimplert(obj)
        })
    },
    fetchAccountingStuff() {
      const params = {
        sort: [{
          key: 'name',
          direction: 'asc'
        }],
        filter_groups: [
          {
            filters: [
              ['country_iso_code', 'eq', this.company.country_iso_code],
              ['catalog', 'in', ['customer_types', 'document_types', 'states', 'tax_conditions']]
            ]
          }
        ]
      }
      this.$http.get(this.$appConfig.api.baseUrl + 'accounting-catalogs', { params })
        .then(response => {
          this.accountingCatalogs = response.body.data
        })
    }
  }
}
</script>

<style lang="scss">
.logo-container, .no-logo-container {
  height: 100%;
  max-height: 200px;
  background: transparent no-repeat 50% 0;
  background-size: contain;
}
</style>
